import NewStreamModal from '@anm/components/stream/NewStreamModal';
import useDeleteGuestDestinations from '@anm/components/stream/NewStreamModal/hooks/useDeleteGuestDestinations';
import useDestinationTimeErrors from '@anm/components/stream/NewStreamModal/hooks/useDestinationTimeErrors';
import useSaveDestinationsMetaParams from '@anm/components/stream/NewStreamModal/hooks/useSaveDestinationsMetaParams';
import useStreamDate from '@anm/components/stream/NewStreamModal/hooks/useStreamDate';
import AddDestinationWithModals from '@anm/components/stream/destination_management/AddDestinationWithModals';
import useSelectedDestinations from '@anm/components/stream/destination_management/DestinationAccountList/hooks/useSelectedDestinations';
import isMobile from '@anm/helpers/is/isMobile';
import useSwitchState from '@anm/hooks/useSwitchState';
import useTextAreaOrInputState from '@anm/hooks/useTextAreaOrInputState';
import { useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import config from '../../../config';
import useCanAddGuestDestination from '../../../hooks/streaming/useCanAddGuestDestination';
import useDefaultPreview from '../../../hooks/streaming/useDefaultPreview';
import useDestinationLimit from '../../../hooks/streaming/useDestinationLimit';
import useFetchRoomSettings from '../../../hooks/streaming/useFetchRoomSettings';
import useInitialStreamState from '../../../hooks/streaming/useInitialStreamState';
import useSelectFolderPath from '../../../hooks/streaming/useSelectFolderPath';
import useStreamFolderPath from '../../../hooks/streaming/useStreamFolderPath';
import useStreamTitle from '../../../hooks/streaming/useStreamTitle';
import useSubmitRecording from '../../../hooks/streaming/useSubmitRecording';
import useSubmitStream, { useIsSubmitPending } from '../../../hooks/streaming/useSubmitStream';
import useUpdateUpload from '../../../hooks/streaming/useUpdateUpload';
import useMapStreamDestinations from '../../../hooks/useMapStreamDestinations';
import { projectActions } from '../../../store/modules/project';
import { userSelectors } from '../../../store/modules/user';

import { StreamModalProps } from './types';

export * from './types';

const { urls } = config();

export const STREAM_TIME_INTERVAL = 5;

const StreamModalWrapper: FC<StreamModalProps> = ({
  isEdit,
  roomId,
  isHosted,
  streamId,
  projectId,
  webinarId,
  initPreview,
  isSetupStream,
  onClose,
  isDuplicate,
  videoSrc = '',
  isWebinarSetup,
  streamDestinations,
  isEditDestinations
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(projectActions.toggleProjectStreamModal(true));
    return () => {
      dispatch(projectActions.toggleProjectStreamModal(false));
    };
  }, []);

  useFetchRoomSettings(roomId);
  const [canGuestAddDestination, handelAddGuestDestinationChange] = useCanAddGuestDestination(roomId, isEdit);
  const [streamInitialState, hostedVideoId] = useInitialStreamState(streamId);
  const [title, handleTitleChange, setTile] = useStreamTitle(projectId);
  const [description, handleDescriptionChange, , setDescription] = useTextAreaOrInputState();

  const userProfile = useSelector(userSelectors.selectUserProfile);
  const [destinations, guestsDestinations] = useMapStreamDestinations(
    streamId,
    streamDestinations,
    userProfile?.userId
  );

  const limitProps = useDestinationLimit();
  const [
    destinationIds,
    handleSelectDestination,
    isDestinationLimitMessage,
    setSelectedDestinations
  ] = useSelectedDestinations({ list: destinations, defaultState: [], ...limitProps });
  const [destinationParams, handleSaveDestinationsParams, setDestinationsParams] = useSaveDestinationsMetaParams({
    title,
    description,
    destinations,
    selectedDestinations: destinationIds
  });

  const [isWebinar, , , toggleWebinar] = useSwitchState(!!webinarId || isWebinarSetup);
  const [isScheduled, , , toggleScheduled] = useSwitchState(!!streamInitialState?.schedule);
  const [date, handleDateChange, setSelectedDate] = useStreamDate(streamInitialState?.schedule);
  const [path, folderId, handleOpenTree] = useSelectFolderPath(streamInitialState?.folderId);
  const streamPath = useStreamFolderPath(path, projectId, folderId);
  const [destinationErrors] = useDestinationTimeErrors({
    date,
    isScheduled,
    destinations,
    destinationIds
  });
  const handleUpdateUpload = useUpdateUpload();

  const defaultPreview = useDefaultPreview(projectId, streamInitialState?.preview || initPreview);

  const [isSubmitButtonPending, isUploadingPending] = useIsSubmitPending();

  const [isRecordingPending, handleSubmitRecording] = useSubmitRecording({
    title,
    isEdit,
    roomId,
    folderId,
    streamId,
    isDuplicate,
    streamInitialState
  });

  const [deleteGuestDestinations, handelDeleteGuestDestination] = useDeleteGuestDestinations();

  const handleSubmit = useSubmitStream({
    date,
    title,
    isEdit,
    roomId,
    streamId,
    folderId,
    videoSrc,
    isWebinar,
    isDuplicate,
    description,
    isScheduled,
    destinations,
    destinationIds,
    destinationParams,
    canGuestAddDestination,
    deleteGuestDestinations,
    setTile,
    setDescription,
    setSelectedDate,
    setDestinationsParams,
    setSelectedDestinations,
    streamInitialState,
    projectId: projectId || (hostedVideoId as string)
  });

  const destinationsWithoutDeleted = destinations.filter(
    d => !deleteGuestDestinations.includes(d.id) || d.user_id === userProfile?.userId
  );

  const selectedDestinationIds = destinationIds;
  const guestDestinationsIds = Object.keys(guestsDestinations || {});
  const canShowDestinationTabs = !!guestDestinationsIds.length;

  const waveUrls = {
    waveUrl: urls.wave,
    waveApiUrl: urls.apiWave
  };

  const isWebinarEnabled = useSelector(userSelectors.selectIsWebinarsEnabled);

  return (
    <NewStreamModal
      {...{
        date,
        path,
        isEdit,
        onClose,
        isHosted,
        folderId,
        waveUrls,
        isWebinar,
        isScheduled,
        isDuplicate,
        isSetupStream,
        defaultPreview,
        toggleScheduled,
        isWebinarEnabled,
        destinationErrors,
        destinationParams,
        isEditDestinations,
        canGuestAddDestination,
        selectedDestinationIds,
        isDestinationLimitMessage
      }}
      isStreamOwner
      isCloseButtonInsideModal={isMobile()}
      isDestinationTabs={canShowDestinationTabs}
      userId={userProfile?.userId}
      onSelectPath={handleOpenTree}
      name={title}
      path={streamPath}
      deleteGuestDestinations={deleteGuestDestinations}
      guestDestinationsIds={guestDestinationsIds}
      description={description}
      destinationsList={destinationsWithoutDeleted}
      initialModalState={streamInitialState}
      isUploading={isUploadingPending}
      isSubmitPending={isSubmitButtonPending || isRecordingPending}
      onCreateStream={handleSubmit}
      onDateChange={handleDateChange}
      onWebinarChange={toggleWebinar}
      onNameChange={handleTitleChange}
      onUploadUpdate={handleUpdateUpload}
      onCreateRecording={handleSubmitRecording}
      onSelectDestination={handleSelectDestination}
      onDescriptionChange={handleDescriptionChange}
      onUpdateDestinationParams={setDestinationsParams}
      onSubmitDestinationMeta={handleSaveDestinationsParams}
      onAddGuestDestinationChange={handelAddGuestDestinationChange}
      onDeleteGuestDestination={handelDeleteGuestDestination}
      renderAddDestination={() => (
        <AddDestinationWithModals waveUrl={urls.wave} waveApiUrl={urls.apiWave} isTextButton={false} isInModal={true} />
      )}
    />
  );
};

export default StreamModalWrapper;
